"use client";

import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { Mic, Square, Loader2 } from "lucide-react";

export interface VoiceInputButtonHandle {
  start: () => void;
  stop: () => void;
}

interface VoiceInputButtonProps {
  onTranscript?: (text: string) => void;
  onFinalTranscript?: (text: string) => void;
  onInterimTranscript?: (text: string) => void;
  onListeningChange?: (listening: boolean) => void;
  onError?: (error: string) => void;
  onVoiceStart?: () => void;
  onVoiceStop?: () => void;
  autoRestart?: boolean;
  className?: string;
  disabled?: boolean;
  color?: string;
}

function VoiceInputButtonInner(
  {
    onTranscript,
    onFinalTranscript,
    onInterimTranscript,
    onListeningChange,
    onError,
    onVoiceStart,
    onVoiceStop,
    autoRestart = false,
    className = "",
    disabled = false,
    color = "#6366f1",
  }: VoiceInputButtonProps,
  ref: React.ForwardedRef<VoiceInputButtonHandle>
) {
  const userStoppedRef = useRef(false);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef<any>(null);
  const finalRef = useRef("");
  const interimRef = useRef("");
  const finalSentRef = useRef(false);
  const restartRef = useRef(false);
  const activeRef = useRef(false);
  const lastSentRef = useRef<{ text: string; ts: number } | null>(null);
  // "native" = browser SpeechRecognition (Chrome/Edge/Android Chrome).
  // "recorder" = MediaRecorder + server-side Whisper transcription fallback,
  // for browsers that never implemented SpeechRecognition at all (iOS
  // Safari/WebKit, and anything else built on WebKit on iOS).
  const modeRef = useRef<"native" | "recorder" | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  // iOS Safari/WebKit's MediaRecorder has a known class of bugs where
  // .onstop silently never fires after .stop() is called — with no backstop,
  // that leaves `listening` (and the mic's internal "active" guard) stuck
  // true forever: the glow never turns off, a second tap on the button is a
  // no-op (the guard in start() thinks it's still running), and tapping
  // "stop" again does nothing new because the recorder object itself is
  // already wedged. This timer forces a clean reset if onstop doesn't show
  // up in a reasonable window, regardless of what the recorder is doing.
  const stopWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearStopWatchdog = useCallback(() => {
    if (stopWatchdogRef.current) {
      clearTimeout(stopWatchdogRef.current);
      stopWatchdogRef.current = null;
    }
  }, []);
  const forceReleaseRecorderMode = useCallback(() => {
    clearStopWatchdog();
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    activeRef.current = false;
    setListening(false);
    setTranscribing(false);
  }, [clearStopWatchdog]);

  // A tile closing (or any other unmount) while the mic is live used to
  // leave the watchdog timer and the raw MediaStream both running past the
  // component's lifetime — the timer would fire into dead state setters,
  // and the browser's mic-in-use indicator would stay on with no button
  // left to release it.
  useEffect(() => {
    return () => {
      clearStopWatchdog();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
    };
  }, [clearStopWatchdog]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SR) {
      modeRef.current = "native";
      setSupported(true);
      return;
    }
    const hasRecorder = typeof navigator.mediaDevices?.getUserMedia === "function" && typeof MediaRecorder !== "undefined";
    if (hasRecorder) {
      modeRef.current = "recorder";
      setSupported(true);
      return;
    }
    modeRef.current = null;
    setSupported(false);
  }, []);

  const isSecureContext = typeof window !== "undefined" &&
    (window.location.protocol === "https:" || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  useEffect(() => {
    onListeningChange?.(listening);
  }, [listening, onListeningChange]);

  const stop = useCallback(() => {
    restartRef.current = false;
    userStoppedRef.current = true;

    if (modeRef.current === "recorder") {
      // Stopping the recorder triggers its onstop handler, which uploads
      // the clip for transcription — don't flip `listening` off here,
      // that happens once the recorder actually stops. But onstop is not
      // guaranteed on iOS Safari/WebKit — arm the watchdog so a silent
      // failure here still leaves the button usable instead of stuck.
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
          clearStopWatchdog();
          stopWatchdogRef.current = setTimeout(() => {
            console.warn("[VoiceInput] recorder onstop never fired — forcing reset");
            forceReleaseRecorderMode();
          }, 4000);
        } catch {
          forceReleaseRecorderMode();
        }
      } else {
        forceReleaseRecorderMode();
      }
      onVoiceStop?.();
      return;
    }

    activeRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
    }
    setListening(false);
    onVoiceStop?.();
  }, [onVoiceStop, setListening]);

  const startRecorderMode = useCallback(async () => {
    // Defensive: if a previous session's cleanup was ever incomplete (the
    // watchdog above exists precisely because that can happen), release
    // whatever's left before asking for a new stream — iOS Safari can
    // otherwise hang the new getUserMedia() call indefinitely waiting on
    // a track that's still technically live.
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeCandidates = ["audio/mp4", "audio/webm", "audio/aac", "audio/wav"];
      const mimeType = mimeCandidates.find(
        (t) => typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(t)
      );
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // The watchdog in stop() may have already force-reset everything
        // if this fired late — mediaRecorderRef no longer points at this
        // recorder in that case, so there's nothing left to finish here.
        if (mediaRecorderRef.current !== recorder) return;
        clearStopWatchdog();
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        activeRef.current = false;
        setListening(false);

        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        if (blob.size < 1000) return; // effectively empty — user tapped stop immediately

        setTranscribing(true);
        try {
          const ext = recorder.mimeType?.includes("mp4") ? "mp4" : recorder.mimeType?.includes("wav") ? "wav" : "webm";
          const form = new FormData();
          form.append("audio", blob, `recording.${ext}`);
          const res = await fetch("/api/karma-ac/voice/transcribe", { method: "POST", body: form });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || "Transcription failed");
          const text = (data.text || "").trim();
          if (text) {
            lastSentRef.current = { text, ts: Date.now() };
            onFinalTranscript?.(text);
          }
        } catch (err: any) {
          console.warn("[VoiceInput] transcription error:", err);
          onError?.(err?.message || "Couldn't transcribe your recording.");
        } finally {
          setTranscribing(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      activeRef.current = true;
      userStoppedRef.current = false;
      setListening(true);
      onVoiceStart?.();
    } catch (err: any) {
      console.warn("[VoiceInput] mic access error:", err);
      onError?.(
        err?.name === "NotAllowedError"
          ? "Microphone permission denied. Check browser/site settings."
          : "Couldn't access the microphone."
      );
      activeRef.current = false;
      setListening(false);
    }
  }, [onError, onVoiceStart, onFinalTranscript]);

  const start = useCallback(() => {
    if (!supported || !modeRef.current) {
      onError?.("Voice input isn't supported in this browser.");
      return;
    }
    if (!isSecureContext) {
      onError?.("Voice input needs a secure (HTTPS) connection — this page is loaded over plain HTTP.");
      return;
    }
    // Already running (or auto-restarting) — never spin up a second recognizer
    if (activeRef.current) return;

    if (modeRef.current === "recorder") {
      startRecorderMode();
      return;
    }

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";
    finalRef.current = "";
    interimRef.current = "";
    finalSentRef.current = false;
    userStoppedRef.current = false;

    const normalize = (s: string) => s.toLowerCase().replace(/[\.\,\?\!\;\:]/g, "").trim();

    const sendFinalOnce = (text: string) => {
      const now = Date.now();
      const norm = normalize(text);
      if (
        lastSentRef.current &&
        normalize(lastSentRef.current.text) === norm &&
        now - lastSentRef.current.ts < 6000
      ) {
        return;
      }
      lastSentRef.current = { text, ts: now };
      onFinalTranscript?.(text);
    };

    rec.onresult = (event: any) => {
      let interim = "";
      let gotFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalRef.current += transcript;
          gotFinal = true;
        } else {
          interim += transcript;
        }
      }
      if (interim) {
        interimRef.current = interim;
        const preview = finalRef.current + interim;
        onInterimTranscript?.(preview);
        onTranscript?.(preview);
      }
      if (gotFinal && !finalSentRef.current) {
        finalSentRef.current = true;
        sendFinalOnce(finalRef.current.trim());
      }
    };

    rec.onerror = (event: any) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        const isHttp = typeof window !== "undefined" && window.location.protocol === "http:" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
        const msg = isHttp
          ? "Microphone blocked: this page is HTTP. Voice requires HTTPS."
          : "Microphone permission denied. Check browser settings.";
        console.warn("[VoiceInput]", msg, event.error);
        onError?.(msg);
        restartRef.current = false;
        setListening(false);
        return;
      }
      // no-speech / audio-capture blips are normal during continuous listening — stay alive
      if (event.error === "no-speech" || event.error === "aborted" || event.error === "audio-capture") {
        console.debug("[VoiceInput] transient:", event.error);
        return;
      }
      console.warn("[VoiceInput] error:", event.error);
      onError?.(event.error);
      restartRef.current = false;
      activeRef.current = false;
      setListening(false);
    };

    rec.onend = () => {
      const text = (finalRef.current.trim() || interimRef.current.trim());
      if (text && !finalSentRef.current) {
        finalSentRef.current = true;
        sendFinalOnce(text);
      }
      // Continuous mode: seamlessly restart so the pilot feels always-on (no glow flicker)
      if (restartRef.current && !userStoppedRef.current) {
        finalRef.current = "";
        interimRef.current = "";
        finalSentRef.current = false;
        setTimeout(() => {
          if (restartRef.current && !userStoppedRef.current) {
            try {
              rec.start();
            } catch {
              activeRef.current = false;
              setListening(false);
            }
          }
        }, 150);
        return;
      }
      activeRef.current = false;
      setListening(false);
    };

    recognitionRef.current = rec;
    try {
      rec.start();
      restartRef.current = autoRestart;
      activeRef.current = true;
      setListening(true);
      onVoiceStart?.();
    } catch {
      activeRef.current = false;
      setListening(false);
    }
  }, [supported, autoRestart, isSecureContext, onTranscript, onFinalTranscript, onInterimTranscript, onVoiceStart, onError, startRecorderMode]);

  const toggle = useCallback(() => {
    // Always call start()/stop() rather than early-returning here — start()
    // itself now reports *why* it can't run (unsupported / insecure context)
    // via onError, instead of the click silently doing nothing.
    if (listening) {
      stop();
    } else {
      start();
    }
  }, [listening, start, stop]);

  useImperativeHandle(ref, () => ({ start, stop }), [start, stop]);

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || transcribing}
      title={
        !supported
          ? "Voice input not supported in this browser — tap for details"
          : transcribing
          ? "Transcribing..."
          : listening
          ? modeRef.current === "recorder"
            ? "Stop recording"
            : "Stop listening"
          : "Voice input"
      }
      aria-label={listening ? "Stop listening" : "Voice input"}
      className={`relative p-2 rounded-lg transition min-w-[36px] min-h-[36px] flex items-center justify-center ${className}`}
      style={{
        color: listening ? "#ffffff" : color,
        background: listening ? color : "rgba(255,255,255,0.1)",
        boxShadow: listening ? `0 0 0 2px rgba(255,255,255,0.5), 0 0 20px ${color}` : "none",
        opacity: supported ? 1 : 0.5,
      }}
    >
      {listening && (
        <span
          className="absolute inset-0 rounded-lg animate-ping opacity-40"
          style={{ background: color }}
        />
      )}
      {transcribing ? (
        <Loader2 className="w-4 h-4 relative z-10 animate-spin" />
      ) : listening ? (
        <Square className="w-4 h-4 relative z-10" />
      ) : (
        <Mic className="w-4 h-4 relative z-10" />
      )}
    </button>
  );
}

const VoiceInputButton = forwardRef(VoiceInputButtonInner);
export default VoiceInputButton;
